/**
 * Wrapgram Wishlist (V2) — account-synced, single-list, backed by wishlist-sync (Cloudflare Worker).
 * Replaces the old localStorage-only V1 entirely (not run alongside it). Multi-list (named lists,
 * tabs, "+ New list") was built, shipped, then explicitly removed 2026-10-26 — the backend still
 * has exactly one list per customer under the hood (unchanged, not worth migrating away from),
 * this file just never shows or offers more than that one.
 *
 * Login is required to save — there's no guest/local fallback. Tapping a heart while logged out
 * stashes the pending action and redirects through /pages/login?redirect=<here>, same pattern
 * already used by Buy Now (window.wrapgramRequireLogin in wrapgram-login-session.js). On return,
 * resumePendingAction() finishes the save automatically.
 *
 * Public API kept identical in shape to V1 on purpose: window.WrapgramWishlist.sync() is already
 * called defensively from wrapgram-product-personalization.js (both MutationObservers + the
 * context-changed handler) and wrapgram-load-more.js's shared reinit helper — keeping this exact
 * global name/shape means zero changes needed in either of those files.
 */
(function () {
  'use strict';

  var API_BASE = 'https://wrapgram-wishlist-sync.wrapgram.workers.dev';
  var SESSION_KEY = 'wrapgram_session_token';
  var PENDING_KEY = 'wrapgram_pending_wishlist_action';
  var PENDING_TTL_MS = 10 * 60 * 1000; // ignore a pending action if the login round-trip took longer than this

  var cache = null; // { entries } from the last successful GET /wishlist
  var cachePromise = null;
  var wishlistBadgeEls = null; // [data-wrapgram-wishlist-count] — static per page load, cached once
  var wishlistFailedEntryIds = {}; // entries whose real-card fetch failed this render pass

  // ---- helpers --------------------------------------------------------------------------
  function getToken() {
    try { return localStorage.getItem(SESSION_KEY); } catch (e) { return null; }
  }
  function isLoggedIn() { return !!getToken(); }

  function toProductGid(id) {
    id = String(id || '');
    return id.indexOf('gid://') === 0 ? id : 'gid://shopify/Product/' + id;
  }

  // Card tags are display strings ("Hybrid Case"); the Worker/compatibility metaobject key off
  // the lowercase_underscore form ("hybrid_case") — matches VALID_CASE_TYPES in wishlist-sync.
  function normalizeCaseType(tag) {
    if (!tag) return '';
    return String(tag).trim().toLowerCase().replace(/\s+/g, '_');
  }

  function currentDeviceModel() {
    try { return (window.WrapgramDeviceContext && window.WrapgramDeviceContext.getModel()) || ''; }
    catch (e) { return ''; }
  }

  function apiFetch(path, options) {
    var token = getToken();
    if (!token) return Promise.resolve({ ok: false, error: 'not logged in' });
    options = options || {};
    options.headers = options.headers || {};
    options.headers.Authorization = 'Bearer ' + token;
    if (options.body) options.headers['Content-Type'] = 'application/json';
    return fetch(API_BASE + path, options)
      .then(function (r) { return r.json(); })
      .catch(function () { return { ok: false, error: 'network error' }; });
  }

  function loadWishlist(force) {
    if (!isLoggedIn()) { cache = { entries: [] }; return Promise.resolve(cache); }
    if (cachePromise && !force) return cachePromise;
    cachePromise = apiFetch('/wishlist').then(function (data) {
      cache = data.ok ? data : { entries: [] };
      return cache;
    });
    return cachePromise;
  }

  function findEntryFor(productId) {
    if (!cache) return null;
    var gid = toProductGid(productId);
    for (var i = 0; i < cache.entries.length; i++) {
      if (cache.entries[i].product && cache.entries[i].product.id === gid) return cache.entries[i];
    }
    return null;
  }

  // ---- heart button painting --------------------------------------------------------------
  // size mirrors whatever was already in the DOM (18 on the PDP button, 16 on card buttons) —
  // repainting with a hardcoded size caused a visible icon shrink/flash on every page load the
  // first time sync() ran, since the server-rendered static SVG and this one disagreed.
  function heartSVG(filled, size) {
    size = size || '16';
    var attrs = filled ? 'fill="currentColor"' : 'fill="none" stroke="currentColor" stroke-width="1.8"';
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="' + size + '" height="' + size + '" ' + attrs +
      ' aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';
  }

  function paintButton(btn, saved) {
    btn.setAttribute('data-wishlisted', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
    var labelSpan = btn.querySelector('.wrapgram-wishlist-btn__label');
    var existingSvg = btn.querySelector('svg');
    var size = existingSvg ? existingSvg.getAttribute('width') : null;
    btn.innerHTML = '';
    btn.insertAdjacentHTML('afterbegin', heartSVG(saved, size));
    if (labelSpan) btn.appendChild(labelSpan); // preserve a text label if the markup had one (PDP button)
  }

  function updateHeaderBadge() {
    if (!wishlistBadgeEls) wishlistBadgeEls = document.querySelectorAll('[data-wrapgram-wishlist-count]');
    var count = (cache && isLoggedIn()) ? cache.entries.length : 0;
    wishlistBadgeEls.forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // Re-paints every heart currently in the DOM against the cached wishlist. Safe to call often —
  // never fetches on its own (loadWishlist handles its own caching/dedupe), just reads + repaints,
  // and skips any button already showing the correct state instead of tearing down/rebuilding its
  // SVG unconditionally — this runs on every scroll-batch/device-context-change, so most calls
  // should touch zero buttons.
  // Deliberately does NOT call renderPage(). renderGrid() calls this (not sync()) after injecting
  // real cards: sync() itself calls renderPage(), and renderPage() calls renderGrid() — if
  // renderGrid()'s own completion also called sync(), that's an unterminated loop (confirmed live,
  // 2026-10-26: the grid never settled, it was being reset back to skeleton and re-fetched forever,
  // just fast enough that "resolved, no throw" was all that ever surfaced).
  function paintHearts() {
    var loggedIn = isLoggedIn();
    document.querySelectorAll('[data-wrapgram-wishlist-btn]').forEach(function (btn) {
      var saved = loggedIn && !!findEntryFor(btn.getAttribute('data-product-id'));
      if (btn.getAttribute('data-wishlisted') === String(saved)) return;
      paintButton(btn, saved);
    });
    updateHeaderBadge();
  }

  function sync() {
    if (!isLoggedIn()) { paintHearts(); return; }
    loadWishlist().then(function () {
      paintHearts();
      renderPage(); // no-op unless the wishlist page's container is on this page
    });
  }

  // ---- toggle (the actual save/remove action) ----------------------------------------------
  function readButtonData(btn) {
    var card = btn.closest('[data-wrapgram-card]');
    return {
      productId: btn.getAttribute('data-product-id'),
      caseType: btn.getAttribute('data-wrapgram-case-type') ||
        (card && card.getAttribute('data-wrapgram-case-type')) || '',
    };
  }

  function sendToggle(data) {
    return apiFetch('/wishlist/toggle', {
      method: 'POST',
      body: JSON.stringify({
        productId: toProductGid(data.productId),
        // An explicit deviceModel (set by the cart-drawer move-to-wishlist button from the cart
        // line's own saved _wrapgram_model property) always wins over the globally-selected
        // device — a heart click has no such context and falls back to currentDeviceModel() as
        // before.
        deviceModel: data.deviceModel || currentDeviceModel(),
        caseType: normalizeCaseType(data.caseType),
      }),
    });
  }

  // Applies a toggle response directly to the in-memory cache instead of re-fetching the whole
  // wishlist afterward — that extra GET /wishlist round trip (itself several sequential Admin API
  // calls server-side) was the other real contributor to toggling feeling slow, on top of the
  // network latency of the toggle call itself. The toggle response already carries everything
  // needed (wishlist-sync now returns the full entry object on add, confirmed 2026-10-26) so no
  // follow-up fetch is needed at all for the common case — paints instantly, confirmed shortly
  // after by the toggle call's own response, never a second network round trip.
  function applyToggleResult(res, productId) {
    if (!cache) cache = { entries: [] };
    var gid = toProductGid(productId);
    if (res.action === 'added' && res.entry) {
      cache.entries = cache.entries.filter(function (e) { return !(e.product && e.product.id === gid); });
      cache.entries.push(res.entry);
    } else if (res.action === 'removed') {
      cache.entries = cache.entries.filter(function (e) { return !(e.product && e.product.id === gid); });
    }
    paintHearts();
    updateWishlistPageAfterToggle(res.action, productId);
  }

  // Targeted update for the wishlist page specifically, instead of calling renderPage() (which
  // re-fetches every remaining card's HTML over the network just because one entry's saved state
  // changed — confirmed live 2026-10-26: removing one item from a 20-item list re-triggered 19
  // unrelated card fetches). No-op on every other page (the app container doesn't exist there).
  function updateWishlistPageAfterToggle(action, productId) {
    var app = document.getElementById('wrapgram-wishlist-app');
    if (!app || !cache) return;

    if (action === 'removed') {
      var card = app.querySelector('[data-wrapgram-card][data-product-id="' + String(productId) + '"]');
      var li = card && card.closest('li[data-wrapgram-entry]');
      if (li) li.remove();
    }

    var countEl = document.querySelector('[data-wrapgram-wishlist-total]');
    if (countEl) countEl.textContent = cache.entries.length ? '· ' + cache.entries.length + ' saved' : '';
    var addAllBtn = app.querySelector('[data-wrapgram-add-all]');
    if (addAllBtn) addAllBtn.disabled = !cache.entries.length;

    if (!cache.entries.length) renderPage(); // empty state needs the full empty-state markup
  }

  function toggleFromButton(btn) {
    var data = readButtonData(btn);
    var wasSaved = btn.getAttribute('data-wishlisted') === 'true';
    paintButton(btn, !wasSaved); // optimistic — never block the tap on the network
    btn.classList.add('wrapgram-wishlist-btn--pop');
    setTimeout(function () { btn.classList.remove('wrapgram-wishlist-btn--pop'); }, 350);

    sendToggle(data).then(function (res) {
      // Revert via a full paintHearts(), not just this one button — a failed toggle must leave
      // every on-page button for this product (e.g. a duplicate card elsewhere) back in sync with
      // the actually-unchanged cache, not just the one that was tapped.
      if (!res.ok) { paintHearts(); return; }
      applyToggleResult(res, data.productId);
    });
  }

  // Idempotent "ensure saved" — used by the cart-drawer move-to-wishlist action, which must never
  // accidentally REMOVE an item that's already wishlisted (a plain toggle would). Waits on
  // loadWishlist() first: calling this before the initial GET /wishlist has resolved left `cache`
  // null, so findEntryFor() always returned null and this fell through to a plain toggle —
  // removing an already-saved item instead of protecting it.
  function ensureSaved(data) {
    return loadWishlist().then(function () {
      if (findEntryFor(data.productId)) return { ok: true };
      return sendToggle(data).then(function (res) {
        if (res.ok) applyToggleResult(res, data.productId);
        return res;
      });
    });
  }

  // ---- login gate + resume --------------------------------------------------------------
  function requireLoginThenSave(data) {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        productId: data.productId,
        caseType: data.caseType,
        deviceModel: data.deviceModel || '',
        isMove: !!data.isMove,
        savedAt: Date.now(),
      }));
    } catch (e) {}
    window.location.href = '/pages/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
  }

  // Finds the cart-drawer row a resumed "move to wishlist" action came from (matched by product +
  // case type, the same identity the pending payload itself carries) and removes it the same way
  // the live click handler does — reusing Dawn's own tested cart-removal flow rather than calling
  // the cart API directly.
  function removeMatchingCartLine(pending) {
    var moveBtns = document.querySelectorAll('[data-wrapgram-move-to-wishlist]');
    for (var i = 0; i < moveBtns.length; i++) {
      var btn = moveBtns[i];
      if (btn.getAttribute('data-product-id') !== pending.productId) continue;
      if ((btn.getAttribute('data-wrapgram-case-type') || '') !== (pending.caseType || '')) continue;
      var row = btn.closest('tr, .cart-item');
      var removeBtn = row && row.querySelector('.cart-remove-button');
      if (removeBtn) removeBtn.click();
      return;
    }
  }

  function resumePendingAction() {
    var raw;
    try { raw = localStorage.getItem(PENDING_KEY); } catch (e) { return; }
    if (!raw || !isLoggedIn()) return;
    try { localStorage.removeItem(PENDING_KEY); } catch (e) {}
    var pending;
    try { pending = JSON.parse(raw); } catch (e) { return; }
    if (!pending || !pending.productId) return;
    if (Date.now() - (pending.savedAt || 0) > PENDING_TTL_MS) return;
    sendToggle(pending).then(function (res) {
      if (!res.ok) return;
      applyToggleResult(res, pending.productId);
      // A "move to wishlist" deferred through login previously only ever saved the item — the
      // cart line it was supposed to come OUT of was left behind, so it ended up in both places.
      if (pending.isMove) removeMatchingCartLine(pending);
    });
  }

  // ---- click delegation -------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wrapgram-wishlist-btn]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      if (!isLoggedIn()) { requireLoginThenSave(readButtonData(btn)); return; }
      toggleFromButton(btn);
      return;
    }

    var moveBtn = e.target.closest('[data-wrapgram-move-to-wishlist]');
    if (moveBtn) {
      e.preventDefault();
      e.stopPropagation();
      var data = {
        productId: moveBtn.getAttribute('data-product-id'),
        caseType: moveBtn.getAttribute('data-wrapgram-case-type') || '',
        deviceModel: moveBtn.getAttribute('data-wrapgram-device-model') || '',
      };
      if (!isLoggedIn()) {
        requireLoginThenSave({ productId: data.productId, caseType: data.caseType, deviceModel: data.deviceModel, isMove: true });
        return;
      }
      moveBtn.disabled = true;
      ensureSaved(data).then(function () {
        var row = moveBtn.closest('tr, .cart-item');
        var removeBtn = row && row.querySelector('.cart-remove-button');
        if (removeBtn) removeBtn.click(); // reuse Dawn's own tested cart-removal flow, don't reimplement it
        else moveBtn.disabled = false;
      });
    }
  });

  // ==========================================================================================
  // Wishlist page (/pages/wishlist) — only does anything if #wrapgram-wishlist-app exists.
  //
  // Cards are the REAL card-product.liquid markup, fetched through the Section Rendering API
  // exactly the way sections/wrapgram-editors-picks.liquid already does for the homepage —
  // GET {product_url}?view=wrapgram-card&section_id=wrapgram-card-renderer — not a second,
  // hand-built approximation. Everything that function already wires up on a normal card
  // (Add to Cart/Buy Now delegation, the heart button, the lazy-image placeholder) keeps working
  // on these cards unmodified, for free.
  //
  // The one real wrinkle: a normal card personalizes itself from ONE global device context
  // (window.localStorage wrapgram_model) shared by the whole page. A wishlist grid has a
  // DIFFERENT saved device per card. So after injecting each card, its device model line, its
  // image, and its Add to Cart/Buy Now properties are set explicitly from that entry's own saved
  // deviceModel/caseType — reusing the same underlying resolver functions
  // (wrapgramImageIndexFor, wrapgramGetDisplayName) with an explicit model instead of the global
  // one, rather than duplicating their logic.
  // ==========================================================================================
  var pageState = { sort: 'recent' };
  var wishlistPageRequestId = 0;
  var WISHLIST_FETCH_TIMEOUT = 8000;
  var WISHLIST_BATCH_SIZE = 8; // first viewport's worth; later batches load via the scroll-gated element below

  function sortEntries(entries) {
    var sorted = entries.slice();
    if (pageState.sort === 'price_low') sorted.sort(function (a, b) { return (a.product ? a.product.priceRangeV2.minVariantPrice.amount : 0) - (b.product ? b.product.priceRangeV2.minVariantPrice.amount : 0); });
    else if (pageState.sort === 'price_high') sorted.sort(function (a, b) { return (b.product ? b.product.priceRangeV2.minVariantPrice.amount : 0) - (a.product ? a.product.priceRangeV2.minVariantPrice.amount : 0); });
    else sorted.sort(function (a, b) { return new Date(b.savedAt) - new Date(a.savedAt); });
    return sorted;
  }

  function renderToolbar(container, visibleEntries) {
    container.innerHTML =
      '<label class="wrapgram-wishlist-sort">Sort' +
      '<select data-wrapgram-sort>' +
      '<option value="recent"' + (pageState.sort === 'recent' ? ' selected' : '') + '>Recently added</option>' +
      '<option value="price_low"' + (pageState.sort === 'price_low' ? ' selected' : '') + '>Price: low to high</option>' +
      '<option value="price_high"' + (pageState.sort === 'price_high' ? ' selected' : '') + '>Price: high to low</option>' +
      '</select></label>' +
      '<div class="wrapgram-wishlist-toolbar-actions">' +
      '<button type="button" class="wrapgram-wishlist-share" data-wrapgram-share>' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>' +
      'Share</button>' +
      '<button type="button" class="wrapgram-wishlist-addall" data-wrapgram-add-all' + (visibleEntries.length ? '' : ' disabled') + '>' +
      '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>' +
      'Add all ' + visibleEntries.length + ' to cart</button>' +
      '</div>';
  }

  // ---- brand/model/image resolution for an entry's saved device (not the global one) --------
  function deriveBrandFromModel(model) {
    // Only two brands exist in WrapgramConfig; apple model ids are always "iphone-..." — a
    // cheap, reliable split given that constraint, without needing a third stored field.
    return model && model.indexOf('iphone') === 0 ? 'apple' : 'samsung';
  }

  function resolveEntryImage(rawMedia, caseType, model) {
    if (!rawMedia) return '';
    var urls = rawMedia.split('|');
    var idx = (window.wrapgramImageIndexFor) ? window.wrapgramImageIndexFor(caseType, model) : 0;
    var target = Math.min(idx, urls.length - 1);
    return urls[target] || urls[0] || '';
  }

  // Applies one wishlist entry's saved device context onto its freshly-injected real card —
  // the only step that's genuinely custom to this page, everything else about the card is
  // exactly what card-product.liquid already renders and wires up.
  function personalizeInjectedCard(li, entry) {
    var card = li.querySelector('[data-wrapgram-card]');
    if (!card || !entry.deviceModel) return;

    var brand = deriveBrandFromModel(entry.deviceModel);
    card.setAttribute('data-wrapgram-override-brand', brand);
    card.setAttribute('data-wrapgram-override-model', entry.deviceModel);

    var displayName = window.wrapgramGetDisplayName ? window.wrapgramGetDisplayName(brand, entry.deviceModel) : entry.deviceModel;
    var modelLine = card.querySelector('[data-wrapgram-model-line]');
    if (modelLine) { modelLine.textContent = displayName; modelLine.style.display = ''; }

    var img = card.querySelector('img[data-wrapgram-card-deferred]');
    if (img) {
      var raw = img.getAttribute('data-wrapgram-media');
      var caseType = card.getAttribute('data-wrapgram-case-type') || '';
      var url = resolveEntryImage(raw, caseType, entry.deviceModel);
      // Only strip the deferred marker once a url is actually set — stripping it unconditionally
      // left a card with no resolvable image permanently blank, since nothing else (the normal
      // IntersectionObserver image loader keys off this same marker) would ever come back to it.
      if (url) {
        img.removeAttribute('data-wrapgram-card-deferred');
        img.addEventListener('load', function () { img.classList.remove('wrapgram-img-loading'); }, { once: true });
        img.src = url;
      }
    }

    // Every card here was saved against a real device — always show its own Add to Cart/Buy Now,
    // independent of whether the visitor currently has any device selected site-wide.
    var actions = card.querySelector('[data-wrapgram-card-actions]');
    if (actions) actions.classList.add('wrapgram-card-actions--visible');

    if (entry.backInStock || entry.priceDropped) {
      var media = card.querySelector('.card__media');
      if (media) {
        var pill = document.createElement('div');
        pill.className = 'wrapgram-wishlist-pill ' + (entry.backInStock ? 'wrapgram-wishlist-pill--stock' : 'wrapgram-wishlist-pill--drop');
        pill.textContent = entry.backInStock ? 'Back in stock' : 'Price dropped';
        media.insertBefore(pill, media.firstChild);
      }
    }
  }

  function wishlistSkeletonHTML(count) {
    var html = '';
    for (var i = 0; i < count; i++) html += '<li class="grid__item wrapgram-wishlist-skel"></li>';
    return html;
  }

  // Mirrors wrapgramEpFetchCard in sections/wrapgram-editors-picks.liquid exactly — same
  // endpoint, same timeout-via-abort shape, same "missing card is just skipped" failure mode.
  function fetchRealCard(productUrl) {
    var hasAbort = typeof AbortController !== 'undefined';
    var controller = hasAbort ? new AbortController() : null;
    var timeoutId = controller ? setTimeout(function () { controller.abort(); }, WISHLIST_FETCH_TIMEOUT) : null;
    var sep = productUrl.indexOf('?') === -1 ? '?' : '&';
    return fetch(productUrl + sep + 'view=wrapgram-card&section_id=wrapgram-card-renderer', controller ? { signal: controller.signal } : {})
      .then(function (res) {
        if (timeoutId) clearTimeout(timeoutId);
        if (!res.ok) throw new Error('wishlist: card fetch failed (' + res.status + ')');
        return res.text();
      })
      .catch(function () {
        if (timeoutId) clearTimeout(timeoutId);
        return null;
      });
  }

  function renderWishlistLoadWarning(container, failedCount) {
    var warning = container.parentElement && container.parentElement.querySelector('[data-wrapgram-wishlist-load-warning]');
    var text = failedCount + (failedCount === 1 ? ' item' : ' items') + " couldn't load — try refreshing.";
    if (warning) { warning.textContent = text; return; }
    warning = document.createElement('p');
    warning.setAttribute('data-wrapgram-wishlist-load-warning', '');
    warning.className = 'wrapgram-wishlist-load-warning';
    warning.textContent = text;
    container.insertAdjacentElement('afterend', warning);
  }

  // Loads WISHLIST_BATCH_SIZE entries at a time instead of firing one fetchRealCard per saved
  // entry on page load regardless of count — a 30+-item wishlist previously triggered 30+
  // concurrent Section-Rendering-API requests the instant this page opened, for cards that were
  // mostly off-screen. The first batch (what's actually visible on open) loads immediately; later
  // batches are gated behind the same <wrapgram-load-more> scroll element every other load-on-
  // scroll surface on this site already uses (sections/related-products.liquid, collection grids).
  function renderGrid(container, entries) {
    var requestId = ++wishlistPageRequestId;

    if (!entries.length) {
      container.innerHTML = '<div class="wrapgram-wishlist-empty"><p>Nothing saved here yet.</p><a href="/collections/all" class="wrapgram-btn wrapgram-btn--main">Browse designs</a></div>';
      return;
    }

    var sorted = sortEntries(entries).filter(function (e) { return e.product && e.product.handle; });
    container.innerHTML = '<ul class="grid product-grid grid--4-col-desktop grid--2-col-tablet-down" role="list"></ul>';
    var ul = container.querySelector('ul');
    var queue = sorted.slice();
    var failedCount = 0;
    var loadMoreEl = document.querySelector('[data-wrapgram-wishlist-load-more]');

    function loadBatch() {
      var batch = queue.splice(0, WISHLIST_BATCH_SIZE);
      if (!batch.length) return Promise.resolve({ hasMore: false });

      ul.insertAdjacentHTML('beforeend', wishlistSkeletonHTML(batch.length));
      var skels = Array.prototype.slice.call(ul.querySelectorAll('.wrapgram-wishlist-skel')).slice(-batch.length);

      return Promise.all(batch.map(function (e) { return fetchRealCard('/products/' + e.product.handle); })).then(function (htmls) {
        if (requestId !== wishlistPageRequestId) return { hasMore: false }; // superseded by a newer tab switch/reload

        htmls.forEach(function (html, i) {
          var skel = skels[i];
          if (!html) {
            failedCount++;
            wishlistFailedEntryIds[batch[i].id] = true;
            if (skel) skel.remove();
            return;
          }
          var li = document.createElement('li');
          li.className = 'grid__item';
          li.setAttribute('data-wrapgram-entry', batch[i].id);
          li.innerHTML = html;
          personalizeInjectedCard(li, batch[i]);
          if (skel) ul.replaceChild(li, skel); else ul.appendChild(li);
        });

        if (window.wrapgramInitCardImageObserver) window.wrapgramInitCardImageObserver();
        paintHearts(); // NOT sync() — sync() calls renderPage(), which would re-enter renderGrid()
        if (failedCount > 0) renderWishlistLoadWarning(container, failedCount);

        return { hasMore: queue.length > 0 };
      });
    }

    wishlistFailedEntryIds = {};
    if (loadMoreEl && typeof loadMoreEl.configure === 'function') loadMoreEl.configure({ onLoadMore: loadBatch });
    loadBatch().then(function (result) {
      if (!loadMoreEl) return;
      if (result.hasMore) loadMoreEl.maybeObserve(); else loadMoreEl.hide();
    });
  }

  function renderPage() {
    var app = document.getElementById('wrapgram-wishlist-app');
    if (!app) return;

    if (!isLoggedIn()) {
      window.location.href = '/pages/login?redirect=' + encodeURIComponent('/pages/wishlist');
      return;
    }
    if (!cache) return; // sync() will call back in once loadWishlist resolves

    var toolbarEl = app.querySelector('[data-wrapgram-wishlist-toolbar]');
    var gridEl = app.querySelector('[data-wrapgram-wishlist-grid]');
    var countEl = document.querySelector('[data-wrapgram-wishlist-total]');
    if (!toolbarEl || !gridEl) return;

    var visible = cache.entries; // single list — everything the customer has saved
    if (countEl) countEl.textContent = cache.entries.length ? '· ' + cache.entries.length + ' saved' : '';
    renderToolbar(toolbarEl, visible);
    renderGrid(gridEl, visible);
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-wrapgram-share]')) {
      var url = window.location.href;
      if (navigator.share) { navigator.share({ title: 'My Wrapgram Wishlist', url: url }).catch(function () {}); }
      else if (navigator.clipboard) { navigator.clipboard.writeText(url).then(function () { window.alert('Link copied'); }); }
      return;
    }

    var addAllBtn = e.target.closest('[data-wrapgram-add-all]');
    if (addAllBtn && !addAllBtn.disabled) {
      // Excludes only entries whose card fetch explicitly failed this render pass (not-yet-loaded
      // entries further down the page are still included — they have everything add-to-cart needs
      // straight from the /wishlist response, independent of whether their card HTML ever fetched).
      var visibleEntries = cache.entries.filter(function (en) { return !wishlistFailedEntryIds[en.id]; });
      // Same property shape wrapgramAddToCart() sends for a single item (assets/
      // wrapgram-product-personalization.js) — built per item here rather than forced through
      // that function, which isn't shaped for a batch add, but deliberately not reinventing the
      // property convention itself.
      var items = visibleEntries
        .filter(function (en) { return en.product && en.product.selectedOrFirstAvailableVariant; })
        .map(function (en) {
          // deriveBrandFromModel's heuristic defaults to 'samsung' for anything that isn't an
          // "iphone-..." id — an entry saved with no device context at all must not silently get
          // tagged samsung (personalizeInjectedCard guards the same call with this exact check).
          var brand = en.deviceModel ? deriveBrandFromModel(en.deviceModel) : '';
          var displayName = window.wrapgramGetDisplayName ? window.wrapgramGetDisplayName(brand, en.deviceModel) : (en.deviceModel || '');
          var imageUrl = en.product.featuredMedia && en.product.featuredMedia.preview ? en.product.featuredMedia.preview.image.url : '';
          return {
            id: en.product.selectedOrFirstAvailableVariant.id.split('/').pop(),
            quantity: 1,
            properties: {
              '_wrapgram_brand': brand,
              '_wrapgram_model': en.deviceModel || '',
              '_model': displayName,
              '_uid': Date.now() + '-' + Math.random().toString(36).slice(2, 7),
              '_image_url': imageUrl,
              'Model': displayName,
            },
          };
        });
      if (!items.length) return;
      addAllBtn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }),
      }).then(function (r) {
        if (!r.ok) { addAllBtn.disabled = false; return r.json().then(function (d) { window.alert((d && d.description) || 'Could not add these to your cart.'); }); }
        window.location.href = '/cart';
      });
      return;
    }
  });

  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-wrapgram-sort]')) { pageState.sort = e.target.value; renderPage(); }
  });

  // ---- public API + init ------------------------------------------------------------------
  window.WrapgramWishlist = {
    sync: sync,
    isIn: function (productId) { return !!findEntryFor(productId); },
    reload: function () { return loadWishlist(true).then(sync); },
  };

  function init() {
    resumePendingAction();
    sync();
    renderPage(); // handles its own login-gate redirect even when sync() short-circuits for a logged-out visitor
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
